import { useEffect, useState } from 'react';
import { RECORDINGS, type SentenceExplanation } from '@listen-draw/shared';
import { t, useLocale } from '../lib/i18n.js';
import { loadExplanation } from '../lib/explanations.js';

const roleNames = {
  S: '主語',
  V: '動詞',
  O: '目的語',
  C: '補語',
  M: '修飾・場所など',
  CONJ: '接続',
  EX: '存在を導くthere',
};

/** Mount only after the answer is revealed; no explanation request precedes opening it. */
export function Explanation({ text }: { text: string }) {
  return RECORDINGS[text] ? <ExplanationPanel key={text} text={text} /> : null;
}

function ExplanationPanel({ text }: { text: string }) {
  useLocale();
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState<SentenceExplanation | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!open || data) return;
    const controller = new AbortController();
    void loadExplanation(text, controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [open, text, attempt, data]);

  return (
    <details
      className="sentence-explanation"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        <span>
          {t('Sentence breakdown · Japanese study notes', '正解の英文を解説：構文と日本語訳')}
        </span>
        <small>
          {t('Subject, verb, modifiers and listening clues', '主語・動詞・修飾のつながりまで')}
        </small>
      </summary>
      {open && (
        <div className="explanation-body" lang="ja">
          {!data && !failed && <p role="status">解説を読み込んでいます…</p>}
          {!data && failed && (
            <div>
              <p role="alert">
                解説を読み込めませんでした。接続を確認して、もう一度お試しください。
              </p>
              <button
                className="btn btn--ghost"
                onClick={() => {
                  setFailed(false);
                  setAttempt((n) => n + 1);
                }}
              >
                解説を再読み込み
              </button>
            </div>
          )}
          {data && (
            <>
              <section>
                <h3>日本語訳</h3>
                <p className="explanation-translation">{data.translationJa}</p>
              </section>
              <section>
                <h3>文の骨格</h3>
                <p className="explanation-pattern">{data.patternJa}</p>
                <ol className="syntax-chunks" aria-label="英語の語順と文中の役割">
                  {data.chunks.map((chunk, index) => (
                    <li key={index} data-role={chunk.role}>
                      <span className="syntax-role">
                        {chunk.role} <span>{roleNames[chunk.role]}</span>
                      </span>
                      <strong lang="en">{chunk.text}</strong>
                      <span className="syntax-note">{chunk.noteJa}</span>
                    </li>
                  ))}
                </ol>
                <p className="muted small">
                  S＝主語、V＝動詞、O＝目的語、C＝補語。Mは修飾・場所などを表す補助記号で、すべて省略できるという意味ではありません。
                </p>
              </section>
              <section>
                <h3>構文の読み方</h3>
                <ol className="grammar-points">
                  {data.points.map((point, index) => (
                    <li key={index}>
                      <h4>{point.titleJa}</h4>
                      <p className="grammar-quote" lang="en">
                        {point.quote}
                      </p>
                      <p>{point.bodyJa}</p>
                    </li>
                  ))}
                </ol>
              </section>
              <section>
                <h3>聞き分けの決め手</h3>
                <ul className="explanation-clues">
                  {data.clues.map((clue, index) => (
                    <li key={index}>
                      <span className="muted small">{clue.labelJa}</span>
                      <strong lang="en">{clue.text}</strong>
                      <span>{clue.meaningJa}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      )}
    </details>
  );
}
